import { useNavigate } from "react-router";
import { marked } from "zustand-querystring/format/marked";
import {
  MarketSearchBar,
  type MarketSearchBarProps,
  type MarketSearchSelection,
} from "~/components/marketSearch/MarketSearchBar";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import { useStorefrontBuyerPostcode } from "~/pages/stockList/categoryPage/Storefront/lib/useStorefrontBuyerPostcode";
import { DISCOVER_SEARCH } from "~/constants/routes";
import {
  initialDiscoveryV2SearchStoreState,
  useDiscoveryV2SearchStore,
} from "../v2/DiscoveryV2SearchStore";
import {
  applyMarketSearchSelection,
  stateKeys,
} from "./marketSearchState.shared";
import { DiscoveryInput } from "./DiscoveryInput";

export const DiscoveryMarketSearch = ({
  onSubmit,
  initialValue,
  onCommit,
  inputRef,
  autoFocus,
  fallbackSubmitButtonClassName,
  ...props
}: Omit<MarketSearchBarProps, "onSelect"> & {
  initialValue?: string;
  onSubmit: (value: string) => void;
  onCommit?: (selection: MarketSearchSelection) => void;
  fallbackSubmitButtonClassName?: string;
}) => {
  const enabled = useRemoteFeatureFlags().isActive("marketSearchAutocomplete");
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  const navigate = useNavigate();
  if (!enabled) {
    return (
      <DiscoveryInput
        key={initialValue}
        ref={inputRef}
        autoFocus={autoFocus}
        initialValue={initialValue}
        placeholder={props.placeholder}
        inputClassName={props.inputClassName}
        className={props.className}
        containerClassName={props.inputContainerClassName}
        submitButtonClassName={fallbackSubmitButtonClassName}
        label={props.label}
        onSubmit={onSubmit}
      />
    );
  }
  return (
    <MarketSearchBar
      inputRef={inputRef}
      autoFocus={autoFocus}
      {...props}
      postcode={buyerPostcode}
      onSelect={(selection) => {
        const next = applyMarketSearchSelection(
          selection,
          useDiscoveryV2SearchStore.getState(),
        );
        const url = new URL(window.location.href);
        for (const key of stateKeys) {
          url.searchParams.delete(key);
        }
        const changed = Object.fromEntries(
          stateKeys
            .filter(
              (key) =>
                JSON.stringify(next[key]) !==
                JSON.stringify(initialDiscoveryV2SearchStoreState[key]),
            )
            .map((key) => [key, next[key]]),
        );
        const encoded = Object.entries(marked.stringifyStandalone(changed))
          .flatMap(([key, values]) => values.map((value) => `${key}=${value}`))
          .join("&");
        const search = [url.search.slice(1), encoded].filter(Boolean).join("&");
        navigate({
          pathname: DISCOVER_SEARCH,
          search: search ? `?${search}` : "",
        });
        onCommit?.(selection);
      }}
    />
  );
};
